import { useState, useRef, useEffect } from 'react';
import { getApiUrl } from '../lib/api';
import { motion } from 'framer-motion';
import { springs } from '../lib/animations';
import ScoreBar from '../components/ScoreBar';
import RollingTips from '../components/RollingTips';
import AnimatedButton from '../components/AnimatedButton';
import TypewriterText from '../components/TypewriterText';
import confetti from 'canvas-confetti';
import html2canvas from 'html2canvas';
import NamePromptModal from '../components/NamePromptModal';

interface BudRatingResult {
  id: number;
  appearance: number;
  trichome_density: number;
  color_vibrancy: number;
  trim_quality: number;
  bud_structure: number;
  final_score: number;
  verdict: string;
  image_path: string;
  tips?: string[] | string;
  strain_name?: string | null;
}

export default function RateBud() {
  const [showNameModal, setShowNameModal] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BudRatingResult | null>(null);
  const [error, setError] = useState('');
  const [showScores, setShowScores] = useState(false);
  const [strainName, setStrainName] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const resultCard = useRef<HTMLDivElement>(null);

  const rollerName = localStorage.getItem('rollerName') || '';

  useEffect(() => {
    if (result) {
      setTimeout(() => setShowScores(true), 100);
      if (result.final_score >= 8) {
        setTimeout(() => confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } }), 500);
      }
    }
  }, [result]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setShowScores(false);

    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
      setImageBase64(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRate = async () => {
    if (!imageBase64) return;
    setLoading(true);
    setError('');
    setResult(null);
    setShowScores(false);

    try {
      const res = await fetch(getApiUrl('/api/rate-bud'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rollerName,
          imageBase64,
          strainName: strainName.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Rating failed');
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setImageBase64('');
    setResult(null);
    setShowScores(false);
    setError('');
    setStrainName('');
    if (fileInput.current) fileInput.current.value = '';
    if (cameraInput.current) cameraInput.current.value = '';
  };

  const handleShare = async () => {
    if (!resultCard.current) return;
    try {
      const canvas = await html2canvas(resultCard.current, { backgroundColor: '#1a1f16', scale: 2 });
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return;

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'bud-rating.png', { type: 'image/png' })] })) {
        await navigator.share({
          title: 'Hashups Bud Rating',
          text: `${rollerName} scored ${result?.final_score.toFixed(2)} on a bud!`,
          url: `${window.location.origin}/bud-rating/${result?.id}`,
          files: [new File([blob], 'bud-rating.png', { type: 'image/png' })],
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bud-rating.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      // User cancelled share
    }
  };

  return (
    <div className="max-w-lg mx-auto px-3 py-6">
      <h2 className="text-xl font-bold text-cream mb-0.5">Rate a Bud</h2>
      <p className="text-cream/50 text-xs mb-4">Rating as <span className="text-cannabis-400 font-semibold">{rollerName}</span></p>

      {!result ? (
        <div className="space-y-4">
          {/* Strain name input */}
          <div>
            <label className="block text-cream/60 text-xs mb-1">Strain Name (Optional)</label>
            <input
              type="text"
              value={strainName}
              onChange={e => setStrainName(e.target.value)}
              placeholder="e.g. Blue Dream, OG Kush..."
              className="w-full bg-cannabis-800 border border-cannabis-600 rounded-lg px-3 py-2.5 text-cream text-sm placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400"
            />
          </div>

          {/* Image upload */}
          <div className="border-2 border-dashed border-cannabis-600 rounded-xl p-4 text-center">
            {preview ? (
              <div className="relative inline-block mb-3">
                <img src={preview} alt="Bud preview" className="max-h-56 mx-auto rounded-lg" />
              </div>
            ) : (
              <div className="text-3xl mb-3">◇</div>
            )}

            <div className="flex gap-3">
              <AnimatedButton onClick={() => cameraInput.current?.click()} variant="secondary" className="flex-1 flex items-center justify-center gap-2">
                📷 Camera
              </AnimatedButton>
              <AnimatedButton onClick={() => fileInput.current?.click()} variant="secondary" className="flex-1 flex items-center justify-center gap-2">
                🖼️ Library
              </AnimatedButton>
            </div>

            <input ref={cameraInput} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
            <input ref={fileInput} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </div>

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          <AnimatedButton
            onClick={handleRate}
            disabled={!imageBase64}
            loading={loading}
            size="lg"
            className="w-full"
          >
            {loading ? 'Examining your bud...' : 'Rate This Bud'}
          </AnimatedButton>
        </div>
      ) : (
        <motion.div className="space-y-4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={springs.dramatic}>
          {/* Shareable result card */}
          <div ref={resultCard} className="bg-gradient-to-br from-cannabis-800 to-green-900/40 rounded-xl p-5 border border-cannabis-700">
            <div className="text-center text-cannabis-400 text-xs font-bold mb-2 tracking-widest uppercase">Bud Rater</div>
            {preview && (
              <img src={preview} alt="Rated bud" className="max-h-44 mx-auto rounded-lg mb-3" />
            )}
            <div className="text-center mb-0.5 text-cream/40 text-xs">{rollerName}</div>
            {(result.strain_name || strainName) && (
              <div className="flex justify-center mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cannabis-500/15 text-cannabis-300 border border-cannabis-500/30">
                  ◇ {result.strain_name || strainName}
                </span>
              </div>
            )}

            <div className="text-center mb-4">
              <div className="text-4xl font-bold text-cannabis-300">{result.final_score.toFixed(2)} <span className="text-xl text-cream/40">/ 10</span></div>
              <div className="text-cream/40 text-xs">Final Score</div>
            </div>

            <div className="space-y-2.5">
              <ScoreBar label="Appearance" score={showScores ? result.appearance : 0} />
              <ScoreBar label="Trichome Density" score={showScores ? result.trichome_density : 0} />
              <ScoreBar label="Color Vibrancy" score={showScores ? result.color_vibrancy : 0} />
              <ScoreBar label="Trim Quality" score={showScores ? result.trim_quality : 0} />
              <ScoreBar label="Bud Structure" score={showScores ? result.bud_structure : 0} />
            </div>
          </div>

          {/* Verdict */}
          <div className="bg-cannabis-800/50 rounded-xl p-4 border border-cannabis-700/50">
            <p className="text-cream/90 italic text-base leading-relaxed">
              "<TypewriterText text={result.verdict} />"
            </p>
          </div>

          {/* Tips */}
          {(() => {
            let tips: string[] = [];
            if (Array.isArray(result.tips)) {
              tips = result.tips;
            } else if (typeof result.tips === 'string') {
              try { tips = JSON.parse(result.tips); } catch { tips = []; }
            }
            return tips.length > 0 ? <RollingTips tips={tips} /> : null;
          })()}

          <div className="flex gap-3">
            <AnimatedButton onClick={reset} variant="secondary" className="flex-1">
              Rate Another
            </AnimatedButton>
            <AnimatedButton onClick={handleShare} className="flex-1">
              📤 Share
            </AnimatedButton>
          </div>
        </motion.div>
      )}
    </div>
  );
}
